import { useState } from "react";
import { Alert, Rating, Stack, Typography } from "@mui/material";

export const addDays = (d, n) => {
  const t = new Date(`${d}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() + n);
  return t.toISOString().slice(0, 10);
};
export const overlaps = (a1, a2, b1, b2) => a1 < b2 && b1 < a2;
export const money = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
export const fmtDate = (d) =>
  d
    ? new Date(`${d}T00:00:00Z`).toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      })
    : "";
export const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString() : "");
export const plural = (n, w) => `${n} ${w}${Number(n) === 1 ? "" : "s"}`;

export function nightlyRate(unit, date) {
  for (const s of unit.seasons || []) {
    if (date >= s.start && date <= s.end) return Number(s.price);
  }
  return Number(unit.price || 0);
}

export function quote(unit, start, end) {
  let total = 0;
  let nights = 0;
  for (let d = start; d < end && nights < 400; d = addDays(d, 1)) {
    total += nightlyRate(unit, d);
    nights += 1;
  }
  return { nights, total };
}

export const STATUS = {
  requested: "Waiting for approval",
  approved: "Confirmed",
  declined: "Declined",
  cancelled: "Cancelled",
  checked_in: "Checked in",
  checked_out: "Checked out",
};
export const URGENCY = { low: "Low", normal: "Normal", urgent: "Urgent" };
export const ISSUE_STATUS = { open: "Open", in_progress: "In progress", fixed: "Fixed" };

export function groupBy(list, key) {
  const m = new Map();
  for (const x of list) {
    const k = key(x) || "Other";
    if (!m.has(k)) m.set(k, []);
    m.get(k).push(x);
  }
  return [...m.entries()];
}

function describeNotice(n) {
  return n.ok
    ? `${n.what}: accepted by Kernel for sending (delivery is not confirmed)`
    : `${n.what}: not sent (${n.detail})`;
}

/** Runs one action and keeps what happened, in words, for the person who did it. */
export function useRun(act) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const run = async (op, args, okText) => {
    setBusy(true);
    setMsg(null);
    try {
      const res = await act(op, args);
      const extra = (res?.notices || []).map(describeNotice);
      setMsg({ ok: true, text: [okText, ...extra].filter(Boolean).join(" · ") });
      return res || {};
    } catch (e) {
      setMsg({ ok: false, text: e.message });
      return null;
    } finally {
      setBusy(false);
    }
  };
  return { run, busy, msg };
}

export function Msg({ msg }) {
  if (!msg) return null;
  return (
    <Alert severity={msg.ok ? "success" : "error"} variant="outlined" role={msg.ok ? "status" : "alert"}>
      {msg.ok ? "Done. " : "Not done: "}
      {msg.text}
    </Alert>
  );
}

export function NoticeList({ notices }) {
  if (!notices?.length) return null;
  return (
    <Stack component="ul" spacing={0.5} sx={{ pl: 3, my: 0 }}>
      {notices.map((n, i) => (
        <Typography component="li" variant="body2" key={i}>
          {fmtWhen(n.at)}: {describeNotice(n)}
        </Typography>
      ))}
    </Stack>
  );
}

export function RatingLine({ summary }) {
  if (!summary?.count) {
    return (
      <Typography variant="body2" color="text.secondary">
        No ratings yet
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      <Rating value={summary.avg} precision={0.5} readOnly size="small" aria-hidden="true" />
      <Typography variant="body2">
        {summary.avg} of 5 from {plural(summary.count, "rating")}
      </Typography>
    </Stack>
  );
}
